/**
 * ExecutionHistory - Scrollable list of past code executions
 *
 * This component shows a history of previous executions with:
 * - Collapsible entries showing code preview and status
 * - Expanded view with full code, stdout/stderr, errors, and rich outputs
 * - Re-run button to execute historical code again
 * - Timestamp and duration for each execution
 *
 * Design follows Jupyter notebook pattern - users can scroll back
 * to see what they ran before and re-run with modifications.
 */

import { useState, useMemo } from 'react';
import type { ExecutionResult } from '@deep-agents/shared-types';
import { OutputRenderer } from './OutputRenderer';

// ============================================================================
// TYPES
// ============================================================================

interface ExecutionHistoryProps {
  /** Array of past execution results (oldest first) */
  history: ExecutionResult[];

  /** Called when user clicks re-run button on a history entry */
  onRerun?: (code: string) => void;

  /** Maximum number of entries to display (default: 10) */
  maxVisible?: number;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * ExecutionHistory Component
 *
 * Displays a scrollable list of past executions. Each entry can be expanded
 * to show full details including code, output, errors, and rich results.
 *
 * History is shown newest-first (most recent at top).
 */
export function ExecutionHistory({
  history,
  onRerun,
  maxVisible = 10,
}: ExecutionHistoryProps) {
  // Track which entry is currently expanded (only one at a time)
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Show most recent first, limit to maxVisible entries
  // useMemo prevents re-sorting on every render
  const visibleHistory = useMemo(
    () => [...history].reverse().slice(0, maxVisible),
    [history, maxVisible]
  );

  // ==========================================================================
  // EMPTY STATE
  // ==========================================================================

  if (visibleHistory.length === 0) {
    return (
      <div className="text-center py-6 text-gray-400 text-sm">
        No execution history yet
      </div>
    );
  }

  // ==========================================================================
  // RENDER
  // ==========================================================================

  return (
    <div className="space-y-2">
      {/* Header with count */}
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
          History ({history.length})
        </div>
        {/* Show "X of Y" when history exceeds maxVisible */}
        {history.length > maxVisible && (
          <span className="text-[10px] text-gray-400">
            Showing {maxVisible} of {history.length}
          </span>
        )}
      </div>

      {/* Scrollable list of history entries */}
      <div className="space-y-1 max-h-60 overflow-y-auto">
        {visibleHistory.map((result) => {
          // Determine if this entry is currently expanded
          const isExpanded = expandedId === result.id;

          // Check if this execution had an error
          const hasError = !!result.error;

          // Format the timestamp for display
          const timestamp = new Date(result.executedAt).toLocaleTimeString();

          return (
            <div
              key={result.id}
              className={`
                rounded-lg border transition-all
                ${hasError
                  ? 'border-red-200 bg-red-50/50'
                  : 'border-gray-200 bg-gray-50/50'
                }
              `}
            >
              {/* ========== HEADER (always visible) ========== */}
              <button
                onClick={() => setExpandedId(isExpanded ? null : result.id)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left"
              >
                {/* Expand/collapse chevron icon */}
                <svg
                  className={`w-3 h-3 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>

                {/* Status dot: green for success, red for error */}
                <span
                  className={`w-2 h-2 rounded-full ${
                    hasError ? 'bg-red-500' : 'bg-green-500'
                  }`}
                />

                {/* Code preview - first line, truncated */}
                <code className="flex-1 text-xs text-gray-600 truncate font-mono">
                  {result.code.split('\n')[0].slice(0, 40)}
                  {result.code.length > 40 ? '...' : ''}
                </code>

                {/* Timestamp */}
                <span className="text-[10px] text-gray-400">
                  {timestamp}
                </span>

                {/* Duration in milliseconds */}
                <span className="text-[10px] text-gray-400">
                  {result.durationMs}ms
                </span>
              </button>

              {/* ========== EXPANDED CONTENT ========== */}
              {isExpanded && (
                <div className="px-3 pb-3 space-y-2">
                  {/* Full code with re-run button */}
                  <div className="relative">
                    <pre className="text-xs font-mono bg-gray-800 text-gray-100 p-2 rounded overflow-x-auto max-h-32">
                      {result.code}
                    </pre>
                    {/* Re-run button in top-right corner */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // Don't collapse the entry
                        onRerun?.(result.code);
                      }}
                      className="absolute top-1 right-1 px-2 py-0.5 text-[10px] bg-white/90 rounded text-gray-600 hover:bg-white"
                    >
                      Re-run
                    </button>
                  </div>

                  {/* Standard output (if any) */}
                  {result.stdout.length > 0 && (
                    <div>
                      <div className="text-[9px] text-gray-500 mb-1">stdout</div>
                      <pre className="text-xs font-mono bg-gray-100 text-gray-700 p-2 rounded max-h-20 overflow-auto">
                        {result.stdout.join('')}
                      </pre>
                    </div>
                  )}

                  {/* Standard error (if any) */}
                  {result.stderr.length > 0 && (
                    <div>
                      <div className="text-[9px] text-gray-500 mb-1">stderr</div>
                      <pre className="text-xs font-mono bg-red-50 text-red-700 p-2 rounded max-h-20 overflow-auto">
                        {result.stderr.join('')}
                      </pre>
                    </div>
                  )}

                  {/* Error details (if execution failed) */}
                  {result.error && (
                    <div className="p-2 bg-red-100 rounded text-xs text-red-700">
                      <div className="font-semibold">{result.error.name}: {result.error.value}</div>
                      {result.error.traceback && (
                        <pre className="mt-1 text-[10px] whitespace-pre-wrap opacity-80">
                          {result.error.traceback}
                        </pre>
                      )}
                    </div>
                  )}

                  {/* Rich outputs (charts, tables, etc.) */}
                  {result.results.length > 0 && (
                    <div className="space-y-2">
                      {result.results.map((output, i) => (
                        <OutputRenderer key={i} output={output} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
